import type {
  Fill,
  GameLiveState,
  KalshiOpenOrder,
  KalshiPosition,
  Market,
  MatchedGame,
  NormalizedOrder,
  OrderAck,
  OrderBookSnapshot,
  OrderBookUpdate,
  Trade,
  VenueEvent,
  VenueId,
} from '@rfqsports/shared';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

const TRADE_TAPE_CAP = 50;

// Diagnostics — gated by NEXT_PUBLIC_WS_DEBUG so prod stays quiet. When on,
// we warn whenever a snapshot lands for a market we never subscribed to
// (executor fanout mismatch) or an update lands without a baseline (would
// silently no-op otherwise).
const WS_DEBUG =
  typeof process !== 'undefined' && process.env['NEXT_PUBLIC_WS_DEBUG'] === 'true';

export interface BookState {
  bids: Map<number, number>; // price -> size
  asks: Map<number, number>;
  ts: number;
  seq: number;
}

interface LatencySnap {
  p50: number;
  p95: number;
  n: number;
  ts: number;
}

export type { GameLiveState } from '@rfqsports/shared';

// Mirrors the vault module's snapshot — kept here so the React tree can
// subscribe via Zustand instead of an ad-hoc listener. No key material ever
// lives in this slice.
export interface VaultSlice {
  phase: 'absent' | 'locked' | 'unlocked';
  vaultId: string | null;
  hasKalshi: boolean;
  hasPolymarket: boolean;
  hasPolymarketL2: boolean;
  polymarketAddress: string | null;
  kalshiBalance: number | null;
  testInFlight: boolean;
  testReason: string | null;
  testTs: number | null;
  polymarketBalanceUsd: number | null;
  polymarketAllowanceOk: boolean | null;
  polymarketDeriveInFlight: boolean;
  polymarketDeriveReason: string | null;
  polymarketTestInFlight: boolean;
  polymarketTestReason: string | null;
  polymarketTestTs: number | null;
}

export interface Store {
  connected: boolean;
  // Number of times the WS has dropped without reconnecting yet. Reset to 0
  // on every successful open. UI uses this to distinguish "live → just
  // dropped, retrying" (yellow) from "down for many attempts" (red).
  reconnectAttempts: number;
  markets: Record<VenueId, Market[]>;
  events: Record<VenueId, VenueEvent[]>;
  games: MatchedGame[];
  matchedAt: number;
  // selected outcome for each venue (defaults to first market when markets arrive)
  selected: Partial<Record<VenueId, { marketId: string; outcomeId: string }>>;
  books: Map<string, BookState>;
  trades: Map<string, Trade[]>;
  gameStates: Map<string, GameLiveState>;
  subscriptions: Set<string>;
  latency: Partial<Record<VenueId, LatencySnap>>;
  orderAcks: OrderAck[];
  fills: Fill[];
  errors: string[];
  // PR 10 — every submit goes through submit-actions.ts which registers
  // here. We remove when the matching ack lands. On WS reconnect the
  // remaining entries are replayed (re-signed first, since the original
  // timestamp would be rejected).
  pendingSubmits: Map<string, NormalizedOrder>;
  registerPendingSubmit: (order: NormalizedOrder) => void;
  vault: VaultSlice;
  // Phase 2 PR 3 — refreshed on every relayed snapshot from Kalshi. They
  // live next to the order/ack streams (not inside the vault slice) because
  // they're per-market data, not vault metadata.
  kalshiPositions: KalshiPosition[];
  kalshiOpenOrders: KalshiOpenOrder[];
  kalshiUserRefreshTs: number | null;
  kalshiUserReason: string | null;
  setKalshiUserSnapshot: (s: {
    positions?: KalshiPosition[];
    openOrders?: KalshiOpenOrder[];
    reason?: string;
  }) => void;
  // PR 7 — same shape for Polymarket. PM REST returns looser shapes so we
  // store raw records and let the UI extract fields it cares about.
  polymarketPositions: Record<string, unknown>[];
  polymarketOpenOrders: Record<string, unknown>[];
  polymarketUserRefreshTs: number | null;
  polymarketUserReason: string | null;
  setPolymarketUserSnapshot: (s: {
    positions?: Record<string, unknown>[];
    openOrders?: Record<string, unknown>[];
    reason?: string;
  }) => void;
  setVaultSnapshot: (s: {
    phase: VaultSlice['phase'];
    vaultId: string | null;
    hasKalshi: boolean;
    hasPolymarket: boolean;
    hasPolymarketL2: boolean;
    polymarketAddress: string | null;
  }) => void;
  startVaultTest: () => void;
  setVaultTestResult: (r: { ok: boolean; balance?: number; reason?: string }) => void;
  startPolymarketDerive: () => void;
  setPolymarketDeriveResult: (r: { ok: boolean; reason?: string }) => void;
  startPolymarketTest: () => void;
  setPolymarketTestResult: (r: {
    ok: boolean;
    balanceUsd?: number;
    allowanceOk?: boolean;
    reason?: string;
  }) => void;
  setConnected: (b: boolean) => void;
  setReconnectAttempts: (n: number) => void;
  setMarkets: (venue: VenueId, markets: Market[]) => void;
  setEvents: (venue: VenueId, events: VenueEvent[]) => void;
  setGames: (games: MatchedGame[], matchedAt: number) => void;
  setSelected: (venue: VenueId, sel: { marketId: string; outcomeId: string }) => void;
  markSubscribed: (venue: VenueId, marketId: string, outcomeId: string) => void;
  markUnsubscribed: (venue: VenueId, marketId: string, outcomeId: string) => void;
  applySnapshot: (s: OrderBookSnapshot) => void;
  applyUpdate: (u: OrderBookUpdate) => void;
  addTrade: (t: Trade) => void;
  setGameState: (g: GameLiveState) => void;
  setLatency: (venue: VenueId, snap: LatencySnap) => void;
  addOrderAck: (ack: OrderAck) => void;
  addFill: (fill: Fill) => void;
  addError: (reason: string) => void;
}

export const bookKey = (v: VenueId, m: string, o: string): string => `${v}:${m}:${o}`;

export const useStore = create<Store>()(
  subscribeWithSelector((set) => ({
    connected: false,
    reconnectAttempts: 0,
    markets: { polymarket: [], kalshi: [] },
    events: { polymarket: [], kalshi: [] },
    games: [],
    matchedAt: 0,
    selected: {},
    books: new Map(),
    trades: new Map(),
    gameStates: new Map(),
    subscriptions: new Set(),
    latency: {},
    orderAcks: [],
    fills: [],
    errors: [],
    pendingSubmits: new Map(),
    registerPendingSubmit: (order) =>
      set((state) => {
        const next = new Map(state.pendingSubmits);
        next.set(order.clientOrderId, order);
        return { pendingSubmits: next };
      }),
    vault: {
      phase: 'absent',
      vaultId: null,
      hasKalshi: false,
      hasPolymarket: false,
      hasPolymarketL2: false,
      polymarketAddress: null,
      kalshiBalance: null,
      testInFlight: false,
      testReason: null,
      testTs: null,
      polymarketBalanceUsd: null,
      polymarketAllowanceOk: null,
      polymarketDeriveInFlight: false,
      polymarketDeriveReason: null,
      polymarketTestInFlight: false,
      polymarketTestReason: null,
      polymarketTestTs: null,
    },
    kalshiPositions: [],
    kalshiOpenOrders: [],
    kalshiUserRefreshTs: null,
    kalshiUserReason: null,
    setKalshiUserSnapshot: (s) =>
      set((state) => ({
        kalshiPositions: s.positions ?? state.kalshiPositions,
        kalshiOpenOrders: s.openOrders ?? state.kalshiOpenOrders,
        kalshiUserRefreshTs: Date.now(),
        kalshiUserReason: s.reason ?? null,
      })),
    polymarketPositions: [],
    polymarketOpenOrders: [],
    polymarketUserRefreshTs: null,
    polymarketUserReason: null,
    setPolymarketUserSnapshot: (s) =>
      set((state) => ({
        polymarketPositions: s.positions ?? state.polymarketPositions,
        polymarketOpenOrders: s.openOrders ?? state.polymarketOpenOrders,
        polymarketUserRefreshTs: Date.now(),
        polymarketUserReason: s.reason ?? null,
      })),
    setVaultSnapshot: (s) =>
      set((state) => ({
        vault: {
          ...state.vault,
          phase: s.phase,
          vaultId: s.vaultId,
          hasKalshi: s.hasKalshi,
          hasPolymarket: s.hasPolymarket,
          hasPolymarketL2: s.hasPolymarketL2,
          polymarketAddress: s.polymarketAddress,
          ...(s.phase !== 'unlocked' || !s.hasKalshi
            ? { kalshiBalance: null, testReason: null, testTs: null }
            : {}),
          ...(s.phase !== 'unlocked' || !s.hasPolymarket
            ? {
                polymarketBalanceUsd: null,
                polymarketAllowanceOk: null,
                polymarketTestReason: null,
                polymarketTestTs: null,
                polymarketDeriveReason: null,
              }
            : {}),
        },
      })),
    startVaultTest: () =>
      set((state) => ({ vault: { ...state.vault, testInFlight: true, testReason: null } })),
    setVaultTestResult: (r) =>
      set((state) => ({
        vault: {
          ...state.vault,
          testInFlight: false,
          kalshiBalance: r.ok && r.balance != null ? r.balance : state.vault.kalshiBalance,
          testReason: r.ok ? null : r.reason ?? 'unknown error',
          testTs: Date.now(),
        },
      })),
    startPolymarketDerive: () =>
      set((state) => ({
        vault: { ...state.vault, polymarketDeriveInFlight: true, polymarketDeriveReason: null },
      })),
    setPolymarketDeriveResult: (r) =>
      set((state) => ({
        vault: {
          ...state.vault,
          polymarketDeriveInFlight: false,
          polymarketDeriveReason: r.ok ? null : r.reason ?? 'unknown error',
        },
      })),
    startPolymarketTest: () =>
      set((state) => ({
        vault: { ...state.vault, polymarketTestInFlight: true, polymarketTestReason: null },
      })),
    setPolymarketTestResult: (r) =>
      set((state) => ({
        vault: {
          ...state.vault,
          polymarketTestInFlight: false,
          polymarketBalanceUsd: r.ok && r.balanceUsd != null ? r.balanceUsd : state.vault.polymarketBalanceUsd,
          polymarketAllowanceOk: r.ok && r.allowanceOk != null ? r.allowanceOk : state.vault.polymarketAllowanceOk,
          polymarketTestReason: r.ok ? null : r.reason ?? 'unknown error',
          polymarketTestTs: Date.now(),
        },
      })),
    setConnected: (b) => set({ connected: b }),
    setReconnectAttempts: (n) => set({ reconnectAttempts: n }),
    setMarkets: (venue, markets) =>
      set((state) => {
        const next = { ...state.markets, [venue]: markets };
        const sel = { ...state.selected };
        if (!sel[venue] && markets[0]) {
          sel[venue] = { marketId: markets[0].marketId, outcomeId: markets[0].outcomeId };
        }
        return { markets: next, selected: sel };
      }),
    setEvents: (venue, events) =>
      set((state) => ({ events: { ...state.events, [venue]: events } })),
    setGames: (games, matchedAt) => set({ games, matchedAt }),
    setSelected: (venue, s) =>
      set((state) => ({ selected: { ...state.selected, [venue]: s } })),
    markSubscribed: (venue, marketId, outcomeId) =>
      set((state) => {
        const next = new Set(state.subscriptions);
        next.add(bookKey(venue, marketId, outcomeId));
        return { subscriptions: next };
      }),
    markUnsubscribed: (venue, marketId, outcomeId) =>
      set((state) => {
        const next = new Set(state.subscriptions);
        next.delete(bookKey(venue, marketId, outcomeId));
        return { subscriptions: next };
      }),
    applySnapshot: (s) =>
      set((state) => {
        const books = new Map(state.books);
        const subscriptions = new Set(state.subscriptions);
        const bids = new Map<number, number>();
        const asks = new Map<number, number>();
        for (const [p, sz] of s.bids) if (sz > 0) bids.set(p, sz);
        for (const [p, sz] of s.asks) if (sz > 0) asks.set(p, sz);
        const key = bookKey(s.venue, s.marketId, s.outcomeId);
        if (WS_DEBUG && !subscriptions.has(key)) {
          // eslint-disable-next-line no-console
          console.warn('[store] snapshot for unsubscribed market', {
            venue: s.venue,
            marketId: s.marketId,
            outcomeId: s.outcomeId,
            bids: bids.size,
            asks: asks.size,
          });
        }
        books.set(key, {
          bids,
          asks,
          ts: s.ts,
          seq: s.seq,
        });
        subscriptions.add(key);
        return { books, subscriptions };
      }),
    applyUpdate: (u) =>
      set((state) => {
        const k = bookKey(u.venue, u.marketId, u.outcomeId);
        const cur = state.books.get(k);
        if (!cur) {
          if (WS_DEBUG) {
            // eslint-disable-next-line no-console
            console.warn('[store] update without baseline snapshot', {
              venue: u.venue,
              marketId: u.marketId,
              outcomeId: u.outcomeId,
            });
          }
          return {};
        }
        const bids = new Map(cur.bids);
        const asks = new Map(cur.asks);
        for (const [p, sz] of u.bidChanges) {
          if (Number.isNaN(sz)) continue;
          if (sz <= 0) bids.delete(p);
          else bids.set(p, sz);
        }
        for (const [p, sz] of u.askChanges) {
          if (Number.isNaN(sz)) continue;
          if (sz <= 0) asks.delete(p);
          else asks.set(p, sz);
        }
        const books = new Map(state.books);
        books.set(k, { bids, asks, ts: u.ts, seq: u.seq });
        return { books };
      }),
    addTrade: (t) =>
      set((state) => {
        const trades = new Map(state.trades);
        const k = bookKey(t.venue, t.marketId, t.outcomeId);
        const cur = trades.get(k) ?? [];
        // Newest-first, capped — drop the tail when it overflows so the tape
        // doesn't grow without bound on long-running sessions.
        const next = [t, ...cur].slice(0, TRADE_TAPE_CAP);
        trades.set(k, next);
        return { trades };
      }),
    setGameState: (g) =>
      set((state) => {
        const gameStates = new Map(state.gameStates);
        gameStates.set(g.gameId, g);
        return { gameStates };
      }),
    setLatency: (venue, snap) =>
      set((state) => ({ latency: { ...state.latency, [venue]: snap } })),
    addOrderAck: (ack) =>
      set((state) => {
        const pending = new Map(state.pendingSubmits);
        pending.delete(ack.clientOrderId);
        return {
          orderAcks: [ack, ...state.orderAcks].slice(0, 100),
          pendingSubmits: pending,
        };
      }),
    addFill: (fill) =>
      set((state) => ({ fills: [fill, ...state.fills].slice(0, 250) })),
    addError: (reason) =>
      set((state) => ({ errors: [reason, ...state.errors].slice(0, 50) })),
  })),
);

export interface TopOfBook {
  bid: number | null;
  ask: number | null;
  bidSize: number | null;
  askSize: number | null;
}

export function topOfBook(b: BookState | undefined): TopOfBook {
  if (!b) return { bid: null, ask: null, bidSize: null, askSize: null };
  let bid: number | null = null;
  let bidSize: number | null = null;
  for (const [p, sz] of b.bids) {
    if (!Number.isFinite(sz)) continue;
    if (bid === null || p > bid) {
      bid = p;
      bidSize = sz;
    }
  }
  let ask: number | null = null;
  let askSize: number | null = null;
  for (const [p, sz] of b.asks) {
    if (!Number.isFinite(sz)) continue;
    if (ask === null || p < ask) {
      ask = p;
      askSize = sz;
    }
  }
  return { bid, ask, bidSize, askSize };
}
